import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { filter, map, take } from 'rxjs';
import { realmRoles } from '../services/role.service';

/**
 * Garde de rôle realm. À chaîner APRÈS `authGuard` (qui garantit l'authentification).
 * Si le rôle requis est absent → redirige vers le dashboard contrôleur (UrlTree).
 */
export function roleGuard(required: string): CanActivateFn {
  return () => {
    const oidc = inject(OidcSecurityService);
    const router = inject(Router);
    return oidc.getAccessToken().pipe(
      filter((t) => !!t),
      take(1),
      map((t) => {
        const r = realmRoles(t);
        if (r.includes(required)) {
          return true;
        }
        return router.parseUrl(landingFor(r));
      }),
    );
  };
}

/**
 * Garde de rôle acceptant PLUSIEURS rôles : l'écran est ouvert dès que l'utilisateur porte l'un d'eux.
 *
 * Nécessaire dès qu'une permission serveur est accordée à plusieurs rôles — le plan de charges CAC est
 * tenu par le Secrétariat CAEEP mais consultable par le Président, le Conseil et l'Admin fonctionnel
 * (`cac:read`). Avec {@link roleGuard} mono-rôle, ces rôles avaient l'API sans l'écran.
 */
export function anyRoleGuard(...required: readonly string[]): CanActivateFn {
  return () => {
    const oidc = inject(OidcSecurityService);
    const router = inject(Router);
    return oidc.getAccessToken().pipe(
      filter((t) => !!t),
      take(1),
      map((t) => {
        const r = realmRoles(t);
        if (required.some((role) => r.includes(role))) {
          return true;
        }
        return router.parseUrl(landingFor(r));
      }),
    );
  };
}

/** Espace d'atterrissage d'un porteur de rôles — partagé par les gardes et la route racine. */
function landingFor(roles: readonly string[]): string {
  if (roles.includes('PRESIDENT_CCE')) {
    return '/presidence/dashboard';
  }
  // Le secrétariat TIENT les séances du Conseil : son espace de travail est le Conseil. Un membre du Conseil qui est
  // aussi contrôleur d'État atterrit sur son espace contrôleur, où se fait son travail de tous les jours ; le Conseil
  // reste à un clic, dans le sélecteur d'espaces (PO, 2026-10-09).
  if (
    roles.includes('SECRETARIAT_CONSEIL') ||
    (roles.includes('CONSEIL_CCE') && !roles.includes('CONTROLEUR_ETAT'))
  ) {
    return '/conseil/dashboard';
  }
  if (roles.includes('SECRETARIAT_CAEEP')) {
    // L'espace CAEEP s'ouvre sur son جدول قيادة (décision Q11a du PO, 2026-09-29) : le premier écran de
    // la journée doit dire quoi traiter, pas dérouler un plan de charges de 8 592 px.
    return '/caeep/pilotage';
  }
  if (roles.includes('CELLULE_ADMINISTRATIVE')) {
    return '/cellule/dashboard';
  }
  if (roles.includes('CELLULE_REPORTING')) {
    return '/reporting/dashboard';
  }
  // Les deux organes du dispositif qualité partagent le même espace et le même écran d'entrée :
  // le cockpit des campagnes. Ils ne diffèrent que par leur périmètre et leurs permissions. Un contrôleur
  // d'État qui en est membre (réviseur) atterrit, comme le membre du Conseil, sur son espace contrôleur ;
  // la qualité reste à un clic dans le sélecteur d'espaces (PO, 2026-10-12).
  if (
    (roles.includes('EQUIPE_QUALITE') || roles.includes('CELLULE_REVISION_INTERNE')) &&
    !roles.includes('CONTROLEUR_ETAT')
  ) {
    return '/qualite/campagnes';
  }
  return '/controleur/dashboard';
}

/**
 * Atterrissage par rôle pour la route racine `''` : le Président arrive sur son espace,
 * les autres sur le dashboard contrôleur.
 */
export const landingRedirectGuard: CanActivateFn = () => {
  const oidc = inject(OidcSecurityService);
  const router = inject(Router);
  return oidc.getAccessToken().pipe(
    filter((t) => !!t),
    take(1),
    map((t) => {
      return router.parseUrl(landingFor(realmRoles(t)));
    }),
  );
};
